import styled, { css } from "styled-components";
import Container from "../../atoms/Container/Container.styled";
import {
  StyledButton,
  StyledButtonLink,
} from "../../atoms/Button/Button.styled";
import { withDisplayHeading } from "../../../themes/mixins";
import { device, deviceMax, reducedMotion } from "../../../utils/device";

export const HeroShell = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.brand.deep};
    color: ${theme.colors.white};
    overflow: hidden;
    position: relative;
  `
);

export const HeroGrid = styled(Container)`
  align-items: center;
  display: grid;
  gap: 58px;
  grid-template-columns: 1.05fr 1fr;
  padding: 70px 0 74px;

  @media ${device.desktopXL} {
    gap: 65px;
    padding: 86px 0;
  }

  @media ${deviceMax.desktopSMax} {
    gap: 34px;
  }

  @media ${deviceMax.tabletWideMax} {
    gap: 26px;
    grid-template-columns: 1fr 1fr;
    padding: 44px 0 48px;
  }

  @media ${deviceMax.phoneMax} {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    gap: 38px;
    padding: 40px 0;
  }
`;

export const HeroCopy = styled.div``;

export const HeroTitle = styled.h1(
  ({ theme }) => css`
    ${withDisplayHeading("clamp(44px, 4.42vw, 65px)")};
    letter-spacing: -0.05em;
    line-height: 1.06;

    @media ${device.desktopXL} {
      font-size: 65px;
    }

    @media ${deviceMax.desktopSMax} {
      font-size: 51px;
    }

    @media ${deviceMax.tabletWideMax} {
      font-size: 42px;
    }

    @media ${deviceMax.phoneMax} {
      font-size: 45px;
      max-width: 440px;
    }

    @media ${deviceMax.compactMax} {
      font-size: 41px;
    }

    .accent {
      color: ${theme.colors.brand.cyan};

      &:first-of-type {
        margin-top: 6px;

        @media ${deviceMax.phoneMax} {
          margin-top: 7px;
        }
      }
    }
  `
);

/* max-width is in ch, not px: the two-line break after "decisions —" has to
   hold as font-size changes across breakpoints. Tuned live against the
   current copy (see landing.hero.subhead) rather than assumed from raw
   character count - 38ch fits "...clear decisions —" (35.2ch measured) with
   headroom before "across" would join it (40.6ch measured). Re-measure if
   the copy changes again. */
export const HeroSubhead = styled.p`
  color: ${({ theme }) => theme.colors.brand.onDark.body};
  font-size: 17px;
  line-height: 1.75;
  margin: 26px 0 30px;
  max-width: 38ch;

  @media ${deviceMax.desktopSMax} {
    font-size: 16px;
  }

  @media ${deviceMax.tabletWideMax} {
    font-size: 14px;
    line-height: 1.8;
    margin: 20px 0 24px;
  }

  @media ${deviceMax.phoneMax} {
    font-size: 15px;
    margin: 23px 0 24px;
  }
`;

export const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  @media ${deviceMax.tabletWideMax} {
    gap: 10px;

    ${StyledButton}, ${StyledButtonLink} {
      font-size: 12px;
      min-height: 45px;
      padding: 12px 16px;
    }
  }

  @media ${deviceMax.phoneMax} {
    ${StyledButton}, ${StyledButtonLink} {
      font-size: 13px;
      min-height: 48px;
      padding: 13px 18px;
    }
  }

  @media ${deviceMax.compactMax} {
    ${StyledButton}, ${StyledButtonLink} {
      padding-left: 16px;
      padding-right: 16px;
    }
  }
`;

export const HeroNote = styled.p(
  ({ theme }) => css`
    color: ${theme.colors.brand.onDark.muted};
    font-size: 12px;
    line-height: 1.6;
    margin: 20px 0 0;
    max-width: 410px;

    strong {
      color: #e3eaf2;
      font-weight: ${theme.typography.weights.medium};
    }

    @media ${deviceMax.tabletWideMax} {
      font-size: 10px;
    }

    @media ${deviceMax.phoneMax} {
      font-size: 11px;
      margin-top: 18px;
      max-width: 345px;
    }
  `
);

export const ProductWrap = styled.div`
  padding: 7px 0;
  position: relative;

  &::before {
    background: radial-gradient(
        ellipse at 70% 30%,
        rgba(202, 88, 239, 0.2),
        transparent 60%
      ),
      radial-gradient(
        ellipse at 18% 70%,
        rgba(68, 220, 255, 0.18),
        transparent 65%
      );
    border-radius: 80px;
    content: "";
    inset: -40px -25px;
    pointer-events: none;
    position: absolute;
  }

  @media ${deviceMax.phoneMax} {
    margin: 0 auto;
    max-width: 460px;
    padding: 0;
    width: 100%;
  }
`;

/**
 * Full-bleed white bar, sitting below the dark hero rather than inside it -
 * matches where this strip actually lives in production. TrustBarInner
 * carries the usual content gutter/max-width inside it.
 */
export const TrustBar = styled.section`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.brand.line};
`;

export const TrustBarInner = styled(Container)`
  align-items: center;
  display: flex;
  gap: 15px;
  padding: 26px 0 26px;

  @media ${deviceMax.phoneMax} {
    display: grid;
    gap: 15px;
    grid-template-columns: 1fr;
    padding: 23px 0 23px;
  }
`;

export const StripLabel = styled.p`
  color: ${({ theme }) => theme.colors.brand.muted};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  line-height: 1.6;
  margin: 0;

  span {
    display: block;
  }
`;

export const PartnerLogos = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
`;

/**
 * On the white strip the real logo files (navy/red, black, charcoal - see
 * the pre-redesign Partners section) already read fine on their own, so
 * this is just a click target/hover affordance, not a background badge.
 */
export const PartnerLogoLink = styled.a`
  align-items: center;
  display: flex;
  height: 34px;
  margin-right: 20px;
  opacity: 0.85;
  transition: opacity 0.15s ease, transform 0.15s ease;

  &:hover {
    opacity: 1;
    transform: translateY(-1px);
  }

  @media ${deviceMax.tabletWideMax} {
    height: 30px;
  }

  @media ${reducedMotion} {
    &:hover {
      transform: none;
    }
  }
`;

/**
 * Sized per logo via an inline `width` (see trustedPartners' `crop.windowWidth`)
 * - a fixed window that shows only the logo's own visible bounding box.
 */
export const LogoCropWindow = styled.span`
  display: block;
  height: 23.96px;
  overflow: hidden;
  position: relative;
`;

/**
 * Rendered at its full (padded) natural size and shifted up/left via an
 * inline transform, so only the trimmed, visible region of the source file
 * shows inside LogoCropWindow. See trustedPartners for the per-logo values.
 */
export const LogoCropImage = styled.img`
  display: block;
  left: 0;
  position: absolute;
  top: 0;
`;

